import type {
  AdminMetrics,
  SharedServer,
  Build,
  BuildQueueItem,
  AdminTask,
  AdminTaskDetail,
  B2Project,
  B2ProjectDetail,
  TestTag,
  OdooVersionConfig,
  EnterpriseArchive,
  ContinuousTestingConfig,
  PlaybookRunResult,
} from '@/types/admin';

const ADMIN_BASE = '/api/v1/admin';

async function apiRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${ADMIN_BASE}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
    ...options,
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = data?.message || response.statusText || 'Request failed';
    throw new Error(message);
  }
  return data as T;
}

// Metrics
export async function fetchAdminMetrics(): Promise<AdminMetrics> {
  const data = await apiRequest<{ success: boolean; data: AdminMetrics }>('/metrics');
  return data.data;
}

// Users
export interface AdminUser {
  id: number;
  email: string;
  full_name: string | null;
  admin: boolean;
  role: string;
  email_confirmed: boolean;
  onboarding_completed: boolean;
  deleted: boolean;
  created: string | null;
  last_login: string | null;
  last_activity: string | null;
  has_github: boolean;
  project_count: number;
  signup_ip: string | null;
}

export interface AdminUsersResponse {
  users: AdminUser[];
  pagination: {
    page: number;
    pages: number;
    total: number;
    per_page: number;
  };
  stats: {
    total: number;
    admins: number;
    confirmed: number;
    with_github: number;
  };
}

export async function fetchAdminUsers(params?: {
  page?: number;
  per_page?: number;
  search?: string;
  role?: string;
  status?: string;
}): Promise<AdminUsersResponse> {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.per_page) searchParams.set('per_page', params.per_page.toString());
  if (params?.search) searchParams.set('search', params.search);
  if (params?.role) searchParams.set('role', params.role);
  if (params?.status) searchParams.set('status', params.status);

  const query = searchParams.toString();
  const data = await apiRequest<{ success: boolean; data: AdminUsersResponse }>(
    `/users${query ? `?${query}` : ''}`
  );
  return data.data;
}

export async function sendUserPasswordReset(email: string): Promise<{ success: boolean }> {
  return apiRequest('/users/send-password-reset', { method: 'POST', body: JSON.stringify({ email }) });
}

// Shared servers
export interface SharedServerListResponse {
  servers: { server: SharedServer; load_info?: any; recent_builds_7d?: number; customer_count?: number }[];
  recent_activity: Build[];
  problematic_builds: Build[];
  summary_stats: Record<string, any>;
}

export async function fetchSharedServers(): Promise<SharedServerListResponse> {
  const data = await apiRequest<{ success: boolean; data: SharedServerListResponse }>('/shared-servers');
  return data.data;
}

export async function createSharedServer(payload: {
  server_name: string;
  ip_address: string;
  username: string;
  password?: string;
  private_key?: string;
  port?: number;
  run_server_init?: boolean;
}) {
  return apiRequest('/shared-servers', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function fetchSharedServer(serverId: number): Promise<SharedServer> {
  const data = await apiRequest<{ success: boolean; data: any }>(`/shared-servers/${serverId}`);
  return data.data as SharedServer;
}

export async function updateSharedServer(serverId: number, payload: Partial<SharedServer> & { password?: string; private_key?: string }) {
  return apiRequest(`/shared-servers/${serverId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export async function deleteSharedServer(serverId: number) {
  return apiRequest(`/shared-servers/${serverId}`, { method: 'DELETE' });
}

export async function deleteSharedServerPermanently(serverId: number) {
  return apiRequest(`/shared-servers/${serverId}/delete-permanent`, { method: 'DELETE' });
}

export async function toggleSharedServer(serverId: number) {
  return apiRequest(`/shared-servers/${serverId}/toggle`, { method: 'POST' });
}

export async function configureSharedServer(serverId: number, payload: { max_active_builds: number; max_concurrent_initializing: number; priority_weight: number }) {
  return apiRequest(`/shared-servers/${serverId}/configure-capacity`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function testSharedServerConnection(serverId: number) {
  return apiRequest(`/shared-servers/${serverId}/test-connection`, { method: 'POST' });
}

export async function initializeSharedServer(serverId: number) {
  return apiRequest(`/shared-servers/${serverId}/initialize`, { method: 'POST' });
}

export interface SharedServerBuildsResponse {
  server: SharedServer;
  builds: Build[];
  pagination: { page: number; pages: number; total: number; per_page: number };
  load_info?: any;
}

export async function fetchSharedServerBuilds(serverId: number, page = 1, filters?: { status?: string; project_id?: number }) {
  const params = new URLSearchParams({ page: String(page) });
  if (filters?.status) params.set('status', filters.status);
  if (filters?.project_id) params.set('project_id', String(filters.project_id));
  const data = await apiRequest<{ success: boolean; data: SharedServerBuildsResponse }>(
    `/shared-servers/${serverId}/builds?${params.toString()}`
  );
  return data.data;
}

export async function rebuildSharedServerBuild(buildId: number) {
  return apiRequest(`/shared-servers/builds/${buildId}/rebuild`, { method: 'POST' });
}

export async function destroySharedServerBuild(buildId: number) {
  return apiRequest(`/shared-servers/builds/${buildId}/destroy`, { method: 'POST' });
}

export async function cancelSharedServerBuild(buildId: number) {
  return apiRequest(`/shared-servers/builds/${buildId}/cancel`, { method: 'POST' });
}

export async function retrySharedServerBuild(buildId: number) {
  return apiRequest(`/shared-servers/builds/${buildId}/retry`, { method: 'POST' });
}

export async function fetchSharedServerStats() {
  const data = await apiRequest<{ success: boolean; data: any }>('/shared-servers/stats');
  return data.data;
}

export interface SharedServerQueueResponse {
  queue_items: BuildQueueItem[];
  queue_stats: Record<string, any>;
  project_init_info: Record<string, any>;
}

export async function fetchSharedServerQueue(): Promise<SharedServerQueueResponse> {
  const data = await apiRequest<{ success: boolean; data: SharedServerQueueResponse }>('/shared-servers/queue');
  return data.data;
}

// Tasks
export interface AdminTasksResponse {
  tasks: AdminTask[];
  pagination: { page: number; pages: number; total: number; per_page: number };
  task_types: string[];
  task_statuses: string[];
}

export async function fetchAdminTasks(page = 1, filters?: { status?: string; type?: string; search?: string }) {
  const params = new URLSearchParams({ page: String(page) });
  if (filters?.status) params.set('status', filters.status);
  if (filters?.type) params.set('type', filters.type);
  if (filters?.search) params.set('search', filters.search);
  const data = await apiRequest<{ success: boolean; data: AdminTasksResponse }>(`/tasks?${params.toString()}`);
  return data.data;
}

export async function fetchAdminTask(taskId: number): Promise<AdminTaskDetail> {
  const data = await apiRequest<{ success: boolean; data: AdminTaskDetail }>(`/tasks/${taskId}`);
  return data.data;
}

// B2 credentials
export interface B2ListResponse {
  projects: B2Project[];
  pagination: { page: number; pages: number; total: number; per_page: number };
  summary_stats: Record<string, any>;
}

export async function fetchB2Projects(page = 1, status?: string): Promise<B2ListResponse> {
  const params = new URLSearchParams({ page: String(page) });
  if (status) params.set('status', status);
  const data = await apiRequest<{ success: boolean; data: B2ListResponse }>(`/b2-credentials?${params.toString()}`);
  return data.data;
}

export async function fetchB2Project(projectId: string): Promise<B2ProjectDetail> {
  const data = await apiRequest<{ success: boolean; data: B2ProjectDetail }>(`/b2-credentials/project/${projectId}`);
  return data.data;
}

export async function generateB2(projectId: string) {
  return apiRequest(`/b2-credentials/project/${projectId}/generate`, { method: 'POST' });
}

export async function regenerateB2(projectId: string) {
  return apiRequest(`/b2-credentials/project/${projectId}/regenerate`, { method: 'POST' });
}

export async function deleteB2(projectId: string) {
  return apiRequest(`/b2-credentials/project/${projectId}/delete`, { method: 'POST' });
}

export async function fetchB2Stats() {
  const data = await apiRequest<{ success: boolean; data: any }>('/b2-credentials/stats');
  return data.data;
}

// Test tags
export interface TestTagsResponse {
  tags: TestTag[];
  pagination: { page: number; pages: number; total: number; per_page: number };
  stats: Record<string, number>;
  version_counts: Record<string, number>;
}

export async function fetchTestTags(params: { page?: number; odoo_version?: string; active?: string; status?: string; search?: string } = {}) {
  const query = new URLSearchParams();
  if (params.page) query.set('page', String(params.page));
  if (params.odoo_version) query.set('odoo_version', params.odoo_version);
  if (params.active !== undefined) query.set('active', params.active);
  if (params.status) query.set('status', params.status);
  if (params.search) query.set('search', params.search);
  const data = await apiRequest<{ success: boolean; data: TestTagsResponse }>(`/test-tags?${query.toString()}`);
  return data.data;
}

export async function createTestTags(payload: { tag?: string; tags_bulk?: string; odoo_versions: string[]; description?: string; active?: boolean; status?: string }) {
  return apiRequest('/test-tags', { method: 'POST', body: JSON.stringify(payload) });
}

export async function updateTestTag(tagId: number, payload: Partial<TestTag> & { odoo_versions?: string[] }) {
  return apiRequest(`/test-tags/${tagId}`, { method: 'PATCH', body: JSON.stringify(payload) });
}

export async function deleteTestTag(tagId: number) {
  return apiRequest(`/test-tags/${tagId}/delete`, { method: 'POST' });
}

export async function toggleTestTag(tagId: number) {
  return apiRequest(`/test-tags/${tagId}/toggle`, { method: 'POST' });
}

export async function promoteTestTag(tagId: number) {
  return apiRequest<{ success: boolean; message: string; tag: TestTag }>(`/test-tags/${tagId}/promote`, { method: 'POST' });
}

export async function demoteTestTag(tagId: number) {
  return apiRequest<{ success: boolean; message: string; tag: TestTag }>(`/test-tags/${tagId}/demote`, { method: 'POST' });
}

export interface TestTagPreviewResponse {
  tag_string: string;
  active_tag_string: string;
  staged_tag_string: string;
  combined_tag_string: string;
  active_tags: TestTag[];
  staged_tags: TestTag[];
  tags: TestTag[];
}

export async function previewTestTags(version: string) {
  const data = await apiRequest<{ success: boolean; data: TestTagPreviewResponse }>(`/test-tags/preview/${version}`);
  return data.data;
}

// Odoo versions
export interface OdooVersionsResponse {
  configs: OdooVersionConfig[];
  enterprise_archives: Record<string, EnterpriseArchive | null>;
}

export async function fetchOdooVersions(): Promise<OdooVersionsResponse> {
  const data = await apiRequest<{ success: boolean; data: OdooVersionsResponse }>('/odoo-versions');
  return data.data;
}

export async function fetchOdooVersion(configId: number): Promise<{ config: OdooVersionConfig; archives: EnterpriseArchive[] }> {
  const data = await apiRequest<{ success: boolean; data: { config: OdooVersionConfig; archives: EnterpriseArchive[] } }>(
    `/odoo-versions/${configId}`
  );
  return data.data;
}

export async function updateOdooVersion(configId: number, payload: { odoo_commit_id?: string; enterprise_commit_id?: string; is_active?: boolean; notes?: string; auto_rebuild?: boolean }) {
  return apiRequest(`/odoo-versions/${configId}/update`, { method: 'POST', body: JSON.stringify(payload) });
}

export async function rebuildOdooVersion(configId: number) {
  return apiRequest(`/odoo-versions/${configId}/rebuild`, { method: 'POST' });
}

export async function rebuildAllOdooVersions() {
  return apiRequest('/odoo-versions/rebuild-all', { method: 'POST' });
}

export async function toggleOdooVersion(configId: number) {
  return apiRequest(`/odoo-versions/${configId}/toggle`, { method: 'POST' });
}

export async function fetchActiveOdooConfigs() {
  const data = await apiRequest<{ success: boolean; configs: OdooVersionConfig[] }>('/odoo-versions/api/configs');
  return data.configs;
}

export async function fetchOdooConfig(version: string) {
  const data = await apiRequest<{ success: boolean; config: OdooVersionConfig; latest_archive: EnterpriseArchive | null }>(
    `/odoo-versions/${version}/api/config`
  );
  return data;
}

// Continuous testing
export interface ContinuousTestingResponse {
  configs: {
    config: ContinuousTestingConfig;
    dev_builds_count?: number;
    is_due?: boolean;
    time_until_next_run?: number | null;
  }[];
  available_projects: B2Project[];
}

export async function fetchContinuousTesting(): Promise<ContinuousTestingResponse> {
  const data = await apiRequest<{ success: boolean; data: ContinuousTestingResponse }>('/continuous-testing');
  return data.data;
}

export async function createContinuousTesting(payload: { project_id: number; interval_hours: number; enabled: boolean; discord_webhook_url?: string | null }) {
  return apiRequest('/continuous-testing', { method: 'POST', body: JSON.stringify(payload) });
}

export async function updateContinuousTesting(configId: number, payload: { interval_hours?: number; enabled?: boolean; discord_webhook_url?: string | null }) {
  return apiRequest(`/continuous-testing/${configId}/update`, { method: 'POST', body: JSON.stringify(payload) });
}

export async function deleteContinuousTesting(configId: number) {
  return apiRequest(`/continuous-testing/${configId}/delete`, { method: 'POST' });
}

export async function triggerContinuousTesting(configId: number) {
  return apiRequest(`/continuous-testing/${configId}/trigger`, { method: 'POST' });
}

export async function toggleContinuousTesting(configId: number) {
  return apiRequest(`/continuous-testing/${configId}/toggle`, { method: 'POST' });
}

// Email tools
export async function fetchEmailTemplates(): Promise<string[]> {
  const data = await apiRequest<{ success: boolean; templates: string[] }>('/email/templates');
  return data.templates;
}

export async function sendTestEmail(payload: { destination_email: string; subject?: string; selected_template?: string; json_context?: any; message?: string }) {
  return apiRequest('/email/test', { method: 'POST', body: JSON.stringify(payload) });
}

// Project tools
export async function fetchAdminProjects(): Promise<B2Project[]> {
  const data = await apiRequest<{ success: boolean; projects: B2Project[] }>('/project-tools');
  return data.projects;
}

export async function fetchBranchesForProject(projectId: number) {
  return apiRequest(`/project-tools/fetch-branches/${projectId}`, { method: 'POST' });
}

// Playbook runner
export async function fetchPlaybooks(): Promise<string[]> {
  const data = await apiRequest<{ success: boolean; playbooks: string[] }>('/playbook-runner');
  return data.playbooks;
}

export async function runPlaybook(payload: { hostname: string; username: string; auth_type: 'password' | 'key'; password?: string; ssh_key?: string; playbook: string; extra_vars?: Record<string, any> }): Promise<PlaybookRunResult> {
  const data = await apiRequest<PlaybookRunResult>('/playbook-runner/run', { method: 'POST', body: JSON.stringify(payload) });
  return data;
}
